import {inject, Injectable} from "@angular/core";
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {from, Observable, throwError, timer} from 'rxjs';
import {catchError, retry, switchMap} from 'rxjs/operators';
import {ErrorService} from './error.service';
import {ErrorResponse} from './error.model';
import {AuthService} from "../auth/auth.service";
import {OnlineOfflineService} from "../onlineOffline/online-offline.service";
import {LoggerService} from "../logging/logger.service";
import {ObservabilityService} from "../observability/observability.service";

/**
 * Opts a single request out of this interceptor's retry policy.
 *
 * Only set this on calls that already degrade gracefully when they fail —
 * retrying such a call cannot help the user, but it does multiply the load a
 * failing request puts on the backend.
 *
 * Usage:
 * ```ts
 * this.http.post(url, body, {
 *   context: new HttpContext().set(SKIP_RETRY, true)
 * });
 * ```
 */
export const SKIP_RETRY = new HttpContextToken<boolean>(() => false);

/** Retry attempts made for a retryable request that has not opted out. */
export const RETRY_COUNT = 3;

/** Base delay of the exponential backoff between retry attempts, in milliseconds. */
export const RETRY_BASE_DELAY_MS = 200;

/** Exponential backoff for retry `attempt` (1-based), before jitter: 200/400/800 ms. */
function backoffMs(attempt: number): number {
  return RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
}

/**
 * Delay before retry `attempt` (1-based): exponential backoff on RETRY_BASE_DELAY_MS plus up
 * to RETRY_BASE_DELAY_MS of random jitter, so the clients of a recovering service do not all
 * retry in lockstep. Roughly 200/400/800 ms plus jitter — at most about 2 s in total.
 */
export function retryDelayMs(attempt: number): number {
  return backoffMs(attempt) + Math.random() * RETRY_BASE_DELAY_MS;
}

/**
 * Upper bound of retryDelayMs, letting specs tick deterministically past the jitter. Derived
 * from the same backoff curve rather than restating it: if the two ever drifted, a spec tick
 * would stop covering the real delay and attempt counts would come up silently short.
 */
export function maxRetryDelayMs(attempt: number): number {
  return backoffMs(attempt) + RETRY_BASE_DELAY_MS;
}

/** Shown when the server gives no usable error message and the device is online. */
export const FALLBACK_ERROR_MESSAGE = 'Unable to complete request';

/** Shown when a request fails while the device is offline. */
export const OFFLINE_ERROR_MESSAGE =
  `You're offline and we don't have this information cached, please try again when you're online.`;

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly errorService = inject(ErrorService);
  private readonly onlineOfflineService = inject(OnlineOfflineService);
  private readonly authenticationService = inject(AuthService);
  private readonly logger = inject(LoggerService);
  private readonly observabilityService = inject(ObservabilityService);


  private online: boolean = false;
  private offlineMessageShown = false;

  constructor() {
    const onlineOfflineService = this.onlineOfflineService;

    this.registerToEvents(onlineOfflineService);
  }

  private registerToEvents(onlineOfflineService: OnlineOfflineService) {
    onlineOfflineService.connectionChanged.subscribe(online => {
      this.online = online;
      if (online) {
        this.offlineMessageShown = false;
      }
    });
    onlineOfflineService.isOnline().then(online => { this.online = online });
  }


  /**
   * The instanceof check must come first: an inner interceptor can throw a raw error that is
   * not an HttpErrorResponse (e.g. a TypeError from DirtyDataInterceptor's body inspection),
   * so `.status` must never be read off an arbitrary throwable.
   */
  private isRetryable(error: unknown, method: string): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }
    if (method !== 'GET' && method !== 'HEAD') {
      return false;
    }
    return error.status === 0 || error.status >= 500;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const retryCount = req.context.get(SKIP_RETRY) ? 0 : RETRY_COUNT;

    return next.handle(req)
      .pipe(
        /*
         * Retry policy (HEC-255): only failures that are plausibly transient are retried —
         * network-level failures (status === 0) and 5xx — and only on safe methods (GET and
         * HEAD). A 4xx is deterministic, so retrying cannot change the outcome; it only
         * delays the user's error and multiplies load on an already-degraded backend — the
         * amplification that turned one bad photo batch request into the 2026-08-02 all-pod
         * outage. POST/PUT/PATCH/DELETE are never retried: a write that failed after the
         * server committed would be replayed, risking duplicate writes, and no current write
         * endpoint is known to be idempotent (add an opt-in token if one ever is; SKIP_RETRY
         * remains the opt-out for GETs). Excluding writes also keeps the DirtyDataInterceptor
         * offline-queueing path (registered beneath this interceptor) unreachable from the
         * retry operator. Note isRetryable() admits only status === 0 or 5xx, so a 401 always
         * reaches catchError below on the first failure with no retries spent here.
         */
        retry({
          count: retryCount,
          delay: (error: unknown, attempt: number) => {
            if (!this.isRetryable(error, req.method)) {
              return throwError(() => error);
            }
            return timer(retryDelayMs(attempt));
          }
        }),
        catchError((error: HttpErrorResponse): Observable<HttpEvent<any>> => {
          // this is a bit nasty, but we don't want to log auth token errors to the user as this is actually
          // a valid scenario during account deletion and the user gets logged out and the state of the app
          // is good. This guard wraps the WHOLE recovery-and-reporting path below (not just the
          // toast/Sentry capture) — that is what stops AuthService.handleUnauthorized()'s own
          // refreshToken() call (a POST to this same token endpoint) from recursing back into
          // handleUnauthorized() when the refresh itself fails. Do not narrow or relocate it.
          if (req.url.includes('/protocol/openid-connect/token')) {
            this.logger.info('Throwing error to caller', error);
            return throwError(() => error);
          }

          // HEC-268: a 401 no longer logs the user out unconditionally — a burst of them can
          // arrive together when several requests are in flight, and today's access token can
          // simply have aged out while a perfectly good refresh token sits in storage.
          // handleUnauthorized() makes (at most) one shared refresh attempt for the whole
          // burst and only logs out when that refresh definitively fails (invalid_grant).
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return from(this.authenticationService.handleUnauthorized()).pipe(
              switchMap((recovered): Observable<HttpEvent<any>> => {
                if (recovered) {
                  // Session restored — re-issue the request that 401'd exactly once, with
                  // the token the refresh just produced. No toast or Sentry capture for a
                  // 401 the refresh already resolved, but the re-issue is NOT a second
                  // recovery attempt: a 401 on the retry is reported like any other error
                  // rather than looping back into handleUnauthorized().
                  return next.handle(this.withRefreshedAuthorization(req)).pipe(
                    catchError((retryError: HttpErrorResponse) => {
                      this.reportHttpError(retryError);
                      this.logger.info('Throwing error to caller', retryError);
                      return throwError(() => retryError);
                    })
                  );
                }
                this.reportHttpError(error);
                this.logger.info('Throwing error to caller', error);
                return throwError(() => error);
              })
            );
          }

          this.reportHttpError(error);
          this.logger.info('Throwing error to caller', error);
          return throwError(() => error);
        })
      )

  }

  /**
   * Re-stamps a request with the access token `handleUnauthorized()` has just refreshed.
   *
   * `DefaultOAuthInterceptor` is registered ahead of this interceptor (main.ts) and sets
   * `Authorization` on the request *before* it reaches us, so the `req` captured in
   * intercept() still carries the token that 401'd. Handing that same object back to
   * `next.handle()` would re-send the expired token — downstream of the only interceptor
   * that could have replaced it — so the retry could never do anything but 401 again.
   *
   * Only requests that already carried an `Authorization` header are re-stamped: a URL
   * outside the library's `allowedUrls` never had a bearer token attached, and must not
   * acquire one here just because it happened to answer 401.
   */
  private withRefreshedAuthorization(req: HttpRequest<any>): HttpRequest<any> {
    const token = this.authenticationService.getAuthToken();
    if (!token || !req.headers.has('Authorization')) {
      return req;
    }
    return req.clone({headers: req.headers.set('Authorization', `Bearer ${token}`)});
  }

  /**
   * Reports a non-token-endpoint HTTP error to observability and, unless it is a 401 the
   * caller already recovered from, surfaces a user-facing toast. Split out of intercept()
   * so the 401-recovered path (a successful handleUnauthorized()) can skip this entirely.
   */
  private reportHttpError(error: HttpErrorResponse): void {
    this.observabilityService.captureHttpError(error);

    let errorMessage;
    this.logger.debug(`HTTP Error Handler:`, this.logger.stringify(error));
    if (error.error instanceof ErrorEvent) {
      // client side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // service side error
      this.logger.debug(`HTTP Error:`, this.logger.stringify(error));

      // A 5xx can arrive with an empty body (e.g. a bare 503 from a NotReady pod),
      // in which case error.error is null and the fallback messages below apply.
      const errorResponse = error.error as ErrorResponse | null;
      if (errorResponse?.message) {
        errorMessage = errorResponse.message;
      } else if (this.online) {
        errorMessage = FALLBACK_ERROR_MESSAGE;
      } else {
        errorMessage = OFFLINE_ERROR_MESSAGE;
      }
    }

    this.logger.error(`HTTP Error received: ${errorMessage}`);
    if (!this.online && !this.offlineMessageShown) {
      this.errorService.registerError(errorMessage);
      this.offlineMessageShown = true;
    } else if (this.online) {
      this.errorService.registerError(errorMessage);
    }
  }

}
